import { Loader } from 'components/Widget/Loader';
import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootReducer } from 'redux/reducers';
import { validEmail } from 'utils';
import * as S from './styles';

interface UserAccessManagerProps {
	entityId: string;
	// API functions
	getUsers: (id: string) => Promise<{ emails: string[] }>;
	grantAccess: (id: string, emails: string[]) => Promise<void>;
	revokeAccess: (id: string, email: string) => Promise<void>;
	// Optional callback after granting access (e.g., for analytics)
	onAccessGranted?: () => void;
	// Customization
	errorMessages: {
		fetchFailed: string;
		revokeFailed: string;
	};
}

const UserAccessManager = ({
	entityId,
	getUsers,
	grantAccess,
	revokeAccess,
	onAccessGranted,
	errorMessages,
}: UserAccessManagerProps) => {
	const [userEmails, setUserEmails] = useState<string[]>([]);
	const [displayUsers, setDisplayUsers] = useState<string[]>([]);
	const [emailList, setEmailList] = useState('');
	const [isAddingUsers, setIsAddingUsers] = useState(false);

	// presence of loadingText indicates that the component is loading
	const [loadingText, setLoadingText] = useState<string | null>(null);
	const [removingUser, setRemovingUser] = useState<string | null>(null);

	const { authUser } = useSelector((r: RootReducer) => {
		return {
			authUser: r.mode.authUser,
		};
	}, shallowEqual);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	useEffect(() => {
		const fetchUsers = async () => {
			setLoadingText('Loading users...');
			try {
				const response = await getUsers(entityId);
				setUserEmails(response.emails);
			} catch (error) {
				toast.error(errorMessages.fetchFailed);
			} finally {
				setLoadingText(null);
			}
		};

		fetchUsers();
		// We intentionally only re-fetch when entityId changes.
		// getUsers and errorMessages are inline props that would change every render,
		// but they're just API wrappers and static strings — not closures over changing state.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entityId]);

	useEffect(() => {
		const sortedUsers = userEmails
			.filter((email) => email !== authUser?.email)
			.sort((a, b) => a.localeCompare(b));
		setDisplayUsers(sortedUsers);
	}, [userEmails, authUser?.email]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onRemoveUser = async (email: string) => {
		setRemovingUser(email);
		try {
			await revokeAccess(entityId, email);
			setUserEmails((prev) => prev.filter((e) => e !== email));
			toast.success('User access revoked successfully');
		} catch (error) {
			toast.error(errorMessages.revokeFailed);
		} finally {
			setRemovingUser(null);
		}
	};

	const onAddUser = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!emailList) return;
		const emails: string[] = emailList.split(',').map((email) => email.trim());

		// filter out any emails that already have access
		const emailsWithAccess = userEmails.filter((email) => emails.includes(email));
		if (emailsWithAccess.length === 1) {
			toast.error('The following email address already has access: ' + emailsWithAccess[0]);
			return;
		}
		if (emailsWithAccess.length > 1) {
			toast.error(
				'The following email addresses already have access: ' + emailsWithAccess.join(', ')
			);
			return;
		}

		// filter out any emails that are invalid
		const invalidEmails = emails.filter((email) => !validEmail(email));
		if (invalidEmails.length === 1) {
			toast.error('The following email address is invalid: ' + invalidEmails[0]);
			return;
		}
		if (invalidEmails.length > 1) {
			toast.error('The following email addresses are invalid: ' + invalidEmails.join(', '));
			return;
		}
		if (emails.length === 0) {
			toast.error('No valid email addresses provided');
			return;
		}

		setIsAddingUsers(true);
		try {
			await grantAccess(entityId, emails);
			// we aggressively update the email list the component uses.
			// we expect Stream to soon send an update that matches this
			setUserEmails((prev) => [...prev, ...emails]);
			setEmailList('');
			toast.success('User access granted successfully');
			// Optional callback for analytics or other side effects
			onAccessGranted?.();
		} catch (error) {
			// we don't show an error to the user
			// in practice the server throws an error and that is shown
			// due to the way the forge API is set up
			// However, a console error can't hurt and may help
			console.error('Error granting access', error);
		} finally {
			setIsAddingUsers(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	if (loadingText) {
		return (
			<S.LoadingContainer>
				<Loader size={32} />
				<S.LoadingText>{loadingText}</S.LoadingText>
			</S.LoadingContainer>
		);
	}

	return (
		<>
			<S.AddUserSection as="form" onSubmit={onAddUser}>
				<S.EmailInput
					type="text"
					placeholder="Emails, comma separated"
					value={emailList}
					onChange={(e) => setEmailList(e.target.value)}
					disabled={isAddingUsers}
				/>
				<S.AddButton type="submit" disabled={isAddingUsers || !emailList}>
					{isAddingUsers ? <Loader size={16} /> : 'Add'}
				</S.AddButton>
			</S.AddUserSection>
			<S.Divider />
			<S.SectionTitle>People with access</S.SectionTitle>
			<S.UserList>
				{authUser?.email && <S.UserItem key={authUser.email}>{authUser.email}</S.UserItem>}
				{displayUsers.map((email) => (
					<S.UserItem key={email}>
						{email}
						<S.RemoveButton onClick={() => onRemoveUser(email)} disabled={removingUser === email}>
							{removingUser === email ? <Loader size={16} /> : 'remove'}
						</S.RemoveButton>
					</S.UserItem>
				))}
			</S.UserList>
		</>
	);
};

export default UserAccessManager;
